/**
 * Masternode listing for the WebIS application.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/masternodes/listing.jsx
 * @class SubPageMasterNodes
 * @since 0.0.1
*/

import React, {useState, useEffect} from 'react';
import Web3 from 'web3';
import energiExtension from '@energi/web3-ext';
// import checkPortStatus from 'itsa-browser-portscan';
import {useStore} from 'effector-react';
import {DataTable, DataTableContent, DataTableHead, DataTableRow, DataTableHeadCell, DataTableBody, DataTableCell} from '@rmwc/data-table';
import {Icon} from '@rmwc/icon';
import {LinearProgress} from '@rmwc/linear-progress';
import {Tooltip} from '@rmwc/tooltip';
import {Snackbar, SnackbarAction} from '@rmwc/snackbar';
import {Select} from '@rmwc/select';
import {IconButton} from '@rmwc/icon-button';
import {TextField} from '@rmwc/textfield';
import {Link} from 'gatsby';
import starOpenBlurred from '../../../images/star-open-blurred.svg';
import starOpen from '../../../images/star-open-light.svg';
import star from '../../../images/star.svg';
import metamaskStore from '../../../globals/metamask';
import masternodeOwnerStore from '../../../globals/masternode-owner';
import masternodeListing from '../../../globals/masternode-listing';
import Title from '../../Title';
import Footer from '../../Footer';
import {showIP} from '../../../lib/helpers/enode-ip';
import {networkOptions, explorerUrl as networkExplorerUrl} from '../../../lib/constants/network-info';
import {getStatusText, statusTextToI18nKey} from '../../../lib/helpers/masternode-status-text';
import formatNumber from '../../../lib/helpers/bn-format';
import copyToClipboard from '../../../lib/helpers/clipboard';
import translations from '../../../lib/translations';
import MasternodeDetails from './details';


let HEADERS = [
    {text: 'RANK', field: 'rank', sort: 1, align: 'alignEnd'},
    {text: 'IPv4', field: 'enode', sort: null, align: 'alignEnd'},
    {text: 'STATUS', field: 'isActive', sort: null, align: 'alignStart'},
    {text: 'COLLATERAL', field: 'collateral', sort: null, align: 'alignEnd'},
    {text: 'OWNER', field: 'owner', sort: null, align: 'alignStart'},
    {text: 'VERSION', field: 'sWVersion', sort: null, align: 'alignStart'},
    {text: '', field: '', sort: null, align: ''}];

const
    headerLength = HEADERS.length,
    TABLE_ROW_HEIGHT = 71, // px
    PROGRESS_BAR_HEIGHT = 15, // px
    web3 = new Web3(networkOptions.networkAddress),
    CLASS_STATUS = {
        active: 'is-active',
        offline: 'offline',
        inactive: 'not-active'
    },
    CLASS_VERSION_ICON = {
        latest: <Icon className="version-status latest" icon="check_circle" />,
        outdated: <Icon className="version-status outdated" icon="warning" />,
        critical: <Icon className="version-status critical" icon="report" />
    },
    THOUSAND_WEI_BN = Web3.utils.toBN('1000000000000000000000');
    // HOST_PORTSCAN = 'nodeapi.energi.network',
    // PORT_PORTSCAN = 39797,
    // TIMEOUT_PORTSCAN = 12000;

energiExtension.extend(web3);

const MasternodeListing = props => {
    let data = useStore(masternodeListing.$data),
        isOld = useStore(masternodeListing.$isOldState),
        statistics = useStore(masternodeListing.$stats),
        favorites = useStore(masternodeListing.$favorites),
        currentPayout = useStore(masternodeListing.$currentPayout),
        correctNetwork = useStore(metamaskStore.$correctNetwork),
        metamaskAddress = useStore(metamaskStore.$address),
        [currentSortIndex, setCurrentSortIndex] = useState(HEADERS.findIndex(h => typeof h.sort === 'number')),
        [tableHeaders, setTableHeaders] = useState(HEADERS),
        [snackbarMsg, setSnackbarMsg] = useState(''),
        [filterValue, setFilterValue] = useState(''),
        [showDetails, setShowDetails] = useState(false),
        [selectedItem, setSelectedItem] = useState(null),
        [showSortIcon, setShowSortIcon] = useState(false),
        [simpleMenu, setSimpleMenu] = useState(),
        [explorerUrl, setExplorerUrl] = useState('#'),
        [paginate, setPaginate] = useState({
            displayRows: 10,
            currentPage: 1,
        }),
        [isShown, setIsShown] = useState(false),
        locales = props.langInfo && props.langInfo.locales,
        translate = translations.init(props),
        quickCopyClass = 'quick-copy',
        stat, simpleMenuTop,
        page = paginate.currentPage,
        dataLength = data.length,
        pageLength = paginate.displayRows,
        from = (page - 1) * pageLength + 1,
        to = (page * pageLength > dataLength) ? dataLength : page * pageLength,
        canNextPage, canPrevPage, displayedRecords, rank, addressDeposit,
        // deviceSupportsPortscan = checkPortStatus(HOST_PORTSCAN, PORT_PORTSCAN, true, TIMEOUT_PORTSCAN);
        deviceSupportsPortscan = Promise.resolve(false);

    const compareVersions = (a, b) => {
        const versionA = a.split('.').map(Number);
        const versionB = b.split('.').map(Number);

        for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const partA = versionA[i] || 0;
            const partB = versionB[i] || 0;

            if (partA < partB) return 1; // Version B is greater
            if (partA > partB) return -1; // Version A is greater
        }

        return 0; // Versions are equal
    };

    const getCurrentVersion = (versions) => {
        if (versions.length < 1) {
            return {x:0, y:0, z:0};
        }
        const uniqueVersions = new Set();
        versions.forEach((v) => {
            uniqueVersions.add(v.sWVersion);
        });
        const aVersions = Array.from(uniqueVersions);
        let currentVer = aVersions[0];
        for (let i=1; i<aVersions.length; i++) {
            // temporary fix because we switched from v3->v1
            if (aVersions[i][0]!== '1') {
                continue;
            }
            if (compareVersions(currentVer, aVersions[i]) === 1) {
                currentVer = aVersions[i];
            }
        }
        const versionObject = currentVer.split('.');
        return {
            x: parseInt(versionObject[0], 10),
            y: parseInt(versionObject[1], 10),
            z: parseInt(versionObject[2], 10),
        };
    };

    const getVersionStatus=(item, currVersion) => {
        let versionState, itemVersion = item.split('.');
        itemVersion = {x:itemVersion[0], y:itemVersion[1], z:itemVersion[2]};

        if (itemVersion.x < currVersion.x || itemVersion.y < currVersion.y) {
            versionState = 'critical';
        }
        else if (itemVersion.z<currVersion.z) {
            versionState = 'outdated';
        }
        else {
            versionState = 'latest';
        }
        return versionState;
    };

    const makeMeShorter = (startNum, subtractNum, stringName) => {
        if (stringName && stringName.length > subtractNum) {
            const n = stringName.length;
            const res = stringName.slice(0, startNum);
            return `${res}...${stringName.slice(subtractNum, n)}`;
        }
        return null;
    };

    // replacement for componentDidMount
    // note: MUST have an extra empty array: https://daveceddia.com/useeffect-hook-examples/#only-run-on-mount-and-unmount
    useEffect(() => {
        masternodeListing.run.startPolling();
        document.addEventListener('click', handleDocClick); // do NOT set 3rd argument `true` -> that would make it impossible for clickHandler to stopPropagate
        return () => {
            // will be invoked on unmount
            document.removeEventListener('click', handleDocClick);
            masternodeListing.run.stopPolling();
        };
    }, []);

    const changeRowsPerPage = e => {
        let rows = parseInt(e.target.value, 10);
        setPaginate({
            ...paginate,
            displayRows: rows,
            currentPage: 1
        });
    };

    const changeFilter = e => {
        setFilterValue(e.target.value.toLowerCase().trim());
        setPaginate({
            ...paginate,
            currentPage: 1,
        });
    };

    const nextPage = () => {
        page = ++paginate.currentPage;

        setPaginate({
            ...paginate,
            currentPage: page,
        });
    };

    const prevPage = () => {
        page = --paginate.currentPage;

        setPaginate({
            ...paginate,
            currentPage: page
        });
    };

    const handleDocClick = e => {
        const node = e.target;
        if ((node.tagName !== 'I') || !node.getAttribute('data-owner')) {
            setSimpleMenu(false);
        }
    };

    const getTableHeader = () => {
        return (
            <DataTableHead>
                <DataTableRow>
                    <DataTableHeadCell alignMiddle><img className="icon-star" src={starOpen} /></DataTableHeadCell>
                    {tableHeaders.map((col, index)=> (
                        <DataTableHeadCell
                            alignEnd={col.align === 'alignEnd'}
                            alignMiddle={col.align === 'alignMiddle'}
                            alignStart={col.align === 'alignStart'}
                            className={index === HEADERS.length - 1 ? 'datatable-sticky-cell' : ''}
                            key={index}
                            onSortChange={() => sortTable(index)}
                            sort={showSortIcon && col.sort}>
                            {translate.t(col.text).toUpperCase()}
                        </DataTableHeadCell>
                    ))}
                </DataTableRow>
            </DataTableHead>
        );
    };

    const handleClick = e => {
        const node = e.target;
        let owner, simpleMenuData, nodeTop;
        if (node.tagName === 'I') {
            owner = node.getAttribute('data-owner');
            if (owner) {
                nodeTop = Math.round(node.getBoundingClientRect().top + window.pageYOffset);

                simpleMenuData = {
                    top: nodeTop,
                    owner: node.getAttribute('data-owner')
                };
                setExplorerUrl(networkExplorerUrl + '/address/' + simpleMenuData.owner + '/coin-balances');
                setSimpleMenu(simpleMenuData);
            }
        }
    };

    const handleFavorite = row => {
        let newFavorites = favorites.itsa_deepClone();
        if (newFavorites[row.masternode]) {
            delete newFavorites[row.masternode];
        }
        else {
            newFavorites[row.masternode] = row;
        }
        masternodeListing.on.setFavorites(newFavorites);
    };

    const handleMouseEnter = () => {
        setIsShown(true);
    };

    const handleMouseLeave = () => {
        setIsShown(false);
    };

    const getProgressBar = () => {
        let rowIndexRankOne, progress, collateral, progressValue, progressRewardValue, masternode;
        if (data.length > 0) {
            dataLength = getData().length;
            from = (page - 1) * pageLength + 1,
            to = (page * pageLength > dataLength) ? dataLength : page * pageLength,
            displayedRecords = getData().slice(from - 1, to);
            rowIndexRankOne = displayedRecords.findIndex(row => !row.noRank && (data.indexOf(row) === 0));
            // rowIndexRankOne may be -1 --> then there is no row with rank 1 in the current view
            if (rowIndexRankOne > -1) {
                masternode = displayedRecords[rowIndexRankOne];
                collateral = masternode.collateral.div(THOUSAND_WEI_BN).toString();
                progressValue = collateral ? Math.min(1, currentPayout/collateral) : 0;
                progressRewardValue = Math.round(100 * progressValue);
                progress = <LinearProgress buffer={1} className="swapped" progress={progressValue} reversed/>;
                return (
                    <div className="table-row-reward-progress-bar" style={{top: (rowIndexRankOne + 2) * TABLE_ROW_HEIGHT - PROGRESS_BAR_HEIGHT + 'px'}}>
                        <Tooltip align="top" content={progressRewardValue + translate.t('REWARD_PAID')} open={isShown}>
                            <div style={{position:'absolute', width: '100px', top:'-40px', margin:'0 auto', left:0, right:0}} />
                        </Tooltip>
                        {progress}
                    </div>
                );
            }
        }
    };

    const getTableBody = () => {
        if (data.length > 0) {
            dataLength = getData().length;
            canNextPage = (page * pageLength < dataLength),
            canPrevPage = (page > 1),
            from = (page - 1) * pageLength + 1,
            to = (page * pageLength > dataLength) ? dataLength : page * pageLength,
            displayedRecords = getData().slice(from - 1, to);
            const currentVersion = getCurrentVersion(data);
            console.log(currentVersion);
            return (
                <DataTableBody onClick={handleClick}>
                    {displayedRecords.map(row => {
                        const versionStatus = getVersionStatus(row[HEADERS[5].field], currentVersion);
                        let classname, rank, progressRow;
                        const masternodeStatus = getStatusText(row);
                        if (!row.noRank) {
                            rank = data.indexOf(row) + 1;
                        }
                        if (rank === 1) {
                            classname = 'table-row-reward-indicator';
                        }
                        return (
                            <React.Fragment key={row.masternode}>
                                <DataTableRow className={classname} {...(rank === 1 && {onMouseEnter:handleMouseEnter, onMouseLeave:handleMouseLeave})}>
                                    <DataTableCell alignMiddle>
                                        <Tooltip content={translate.t(favorites[row.masternode] ? 'REMOVE_FROM_FAV' : 'ADD_TO_FAV', true)}>
                                            <img
                                                className="icon-star"
                                                onClick={handleFavorite.bind(null, row)}
                                                src={favorites[row.masternode] ? star : starOpenBlurred} />
                                        </Tooltip>
                                    </DataTableCell>
                                    <DataTableCell alignEnd>{rank}</DataTableCell>
                                    <DataTableCell alignEnd>{showIP(row[HEADERS[1].field])}</DataTableCell>
                                    <DataTableCell alignStart className={CLASS_STATUS[masternodeStatus]}>
                                        {translate.t(statusTextToI18nKey(masternodeStatus))}
                                    </DataTableCell>
                                    <DataTableCell alignEnd>{formatNumber(locales, row[HEADERS[3].field], 0, true)}</DataTableCell>
                                    <DataTableCell alignStart>
                                        {makeMeShorter(12, 32, row[HEADERS[4].field])}
                                    </DataTableCell>
                                    <DataTableCell alignStart>{CLASS_VERSION_ICON[versionStatus]}<span className="version-text">{row[HEADERS[5].field]}</span></DataTableCell>
                                    <DataTableCell
                                        alignMiddle
                                        className={rank === 1 ? 'datatable-sticky-cell rank-one' : 'datatable-sticky-cell'}>
                                        <Icon data-owner={row.owner} icon="more_vert" />
                                    </DataTableCell>
                                </DataTableRow>
                                {progressRow}
                            </React.Fragment>
                        );
                    })}
                </DataTableBody>
            );
        }

        return (
            <DataTableBody>
                <DataTableRow activated="false" className="no-data-row">
                    <DataTableCell alignMiddle colSpan={headerLength}>
                        <span className="no-data-text">{translate.t('NO_MASTERNODE_DATA')}</span>
                    </DataTableCell>
                </DataTableRow>
            </DataTableBody>
        );
    };

    const contextMenu = (owner, liNode) => {
        // first, we need to find out which liNode was clicked:
        if (liNode.tagName.toLowerCase() === 'a') {
            return;
        }
        const ulNode = liNode.parentNode,
            index = Array.prototype.indexOf.call(ulNode.children, liNode),
            item = data.find(record => record.owner === owner);

        let txtWhat = '',
            isCopiedToClipboard = false;

        if (!item || (index === -1)) {
            return;
        }

        switch (index) {
        case 0:
            setSelectedItem(item);
            setShowDetails(true);
            return;
        case 1:
            isCopiedToClipboard = copyToClipboard(owner);
            txtWhat = translate.t('OWNER', true);
            break;
        }

        if (isCopiedToClipboard) {
            setSnackbarMsg(txtWhat + ' ' + translate.t('COPIED_CLIPBOARD'));
        }
        else {
            setSnackbarMsg(translate.t('ACTION_NOT_SUPPORTED', true));
        }
    };

    const getData = () => {
        let filteredData = [...data];
        if ((currentSortIndex !== null) && (currentSortIndex < headerLength)) {
            const field = HEADERS[currentSortIndex].field;
            const favoriteSort = (a, b) => {
                const isFavoriteA = !!favorites[a.masternode];
                const isFavoriteB = !!favorites[b.masternode];
                if (isFavoriteA && !isFavoriteB) {
                    return -1;
                }
                else if (!isFavoriteA && isFavoriteB) {
                    return 1;
                }
                return 0;
            };

            if (field === 'enode') {
                filteredData.sort((a, b) => {
                    const favoriteSortPos = favoriteSort(a, b);
                    if (favoriteSortPos !== 0) {
                        return favoriteSortPos;
                    }
                    if (showIP(a[field]) === showIP(b[field])) {
                        return 0;
                    }
                    if (showIP(a[field]) > showIP(b[field])) {
                        return -HEADERS[currentSortIndex].sort || -1; // enable initial sort
                    }
                    return HEADERS[currentSortIndex].sort || 1; // enable initial sort
                });
            }
            else if (field === 'isActive') {
                filteredData.sort((a, b) => {
                    const favoriteSortPos = favoriteSort(a, b);
                    if (favoriteSortPos !== 0) {
                        return favoriteSortPos;
                    }
                    const x = getStatusText(a),
                        y = getStatusText(b);
                    if (x === y) {
                        return 0;
                    }
                    if (x > y) {
                        return -HEADERS[currentSortIndex].sort || -1; // enable initial sort
                    }
                    return HEADERS[currentSortIndex].sort || 1; // enable initial sort
                });
            }
            else if (field === 'rank') {
                filteredData.sort((a, b) => {
                    const favoriteSortPos = favoriteSort(a, b);
                    if (favoriteSortPos !== 0) {
                        return favoriteSortPos;
                    }
                    if (a === b) {
                        return 0;
                    }
                    if (a < b) {
                        return -HEADERS[currentSortIndex].sort || -1; // enable initial sort
                    }
                    return HEADERS[currentSortIndex].sort || 1; // enable initial sort
                });
            }
            else if (field === 'collateral') {
                filteredData.sort((a, b) => {
                    const favoriteSortPos = favoriteSort(a, b);
                    if (favoriteSortPos !== 0) {
                        return favoriteSortPos;
                    }
                    const x = parseInt(a[field].toString(), 10),
                        y = parseInt(b[field].toString(), 10);
                    if (x === y) {
                        return 0;
                    }
                    if (x > y) {
                        return -HEADERS[currentSortIndex].sort || -1; // enable initial sort
                    }
                    return HEADERS[currentSortIndex].sort || 1; // enable initial sort
                });
            }
            else {
                filteredData.sort((a, b) => {
                    const favoriteSortPos = favoriteSort(a, b);
                    if (favoriteSortPos !== 0) {
                        return favoriteSortPos;
                    }
                    if (a[field] === b[field]) {
                        return 0;
                    }
                    if (a[field] > b[field]) {
                        return -HEADERS[currentSortIndex].sort || -1; // enable initial sort
                    }
                    return HEADERS[currentSortIndex].sort || 1; // enable initial sort
                });
            }
        }

        filteredData = filteredData.filter(d => showIP(d.enode).includes(filterValue) || d.masternode.includes(filterValue) || d.owner.includes(filterValue));
        return filteredData;
    };

    const sortTable = (idx) => {
        // no sort by first and last column
        if ((idx === headerLength - 1) || (data.length < 1)) return;
        // same header
        if (currentSortIndex === idx) {
            HEADERS[idx].sort = HEADERS[idx].sort === null ? 1 : HEADERS[idx].sort * (-1);
        }
        else {
            HEADERS[idx].sort = -1;
            if (currentSortIndex !== null) {
                HEADERS[currentSortIndex].sort = null;
            }
        }

        if (!showSortIcon) {
            setShowSortIcon(true);
        }

        setTableHeaders([...HEADERS]);
        setCurrentSortIndex(idx);
    };

    if (!simpleMenu) {
        quickCopyClass += ' hidden';
    }
    else {
        simpleMenuTop = {top: simpleMenu.top + 'px'};
    }

    if (statistics && statistics.totalCollateral) {
        stat = (
            <>
                <div className="stat-header">
                    <p>{translate.t('MASTERNODE_STATS')}</p>
                </div>

                <div className="pure-g stat-info">
                    <div className="pure-u-1-4">
                        <small>{translate.t('ACTIVE_MASTERNODES', true)}</small>
                        <h4>{statistics.active}</h4>
                    </div>
                    <div className="pure-u-1-4">
                        <small>{translate.t('ACTIVE_COLLATERAL', true)}</small>
                        <h4>{formatNumber(locales, statistics.activeCollateral, 0, true, true, 4)} NRG</h4>
                    </div>
                    <div className="pure-u-1-4">
                        <small>{translate.t('TOTAL_MASTERNODES', true)}</small>
                        <h4>{statistics.total}</h4>
                    </div>
                    <div className="pure-u-1-4">
                        <small>{translate.t('TOTAL_COLLATERAL', true)}</small>
                        <h4>{formatNumber(locales, statistics.totalCollateral, 0, true, true, 4)} NRG</h4>
                    </div>
                </div>
            </>
        );
    }

    if (showDetails) {
        if (!data.noRank) {
            rank = data.indexOf(selectedItem) + 1;
        }
        return (<MasternodeDetails
            {...props}
            data={{...selectedItem, rank}}
            deviceSupportsPortscan={deviceSupportsPortscan}
            setState={setShowDetails} />);
    }

    const progressBar = getProgressBar();

    if (!correctNetwork || !metamaskAddress) {
        addressDeposit = (
            <li>
                <Link onClick={() => masternodeOwnerStore.on.changeAddress(simpleMenu.owner)} to="/masternodes/collateral">
                    {translate.t('COLLATERAL_DEPOSIT_WITHDRAWAL')}
                </Link>
            </li>
        );
    }

    return (
        <div className="masternodes pure-u subpage">
            <Title isOld={isOld} snackbarMessage={translate.t(isOld ? 'SHOWING_OLDDATA' : 'DATA_UPDATED', true)} subTitle={translate.md('masternode-list')} title={translate.t('MASTERNODE_LIST')} />
            <section className="stat">
                {stat}
            </section>
            <section className="listing">
                <TextField
                    className="table-filter"
                    label={translate.t('IP_MN_OWNER')}
                    onChange={changeFilter}
                    outlined
                    trailingIcon="search"
                    value={filterValue} />
                <br /><br /><br />
                <ul
                    className={quickCopyClass}
                    onClick={e => contextMenu(simpleMenu.owner, e.target)}
                    style={simpleMenuTop}>
                    <li>{translate.t('VIEW_MASTERNODE_DETAILS')}</li>
                    <li>{translate.t('COPY_OWNER_ADDRESS')}</li>
                    {addressDeposit}
                    <li>
                        <a
                            href={explorerUrl}
                            onClick={e => e.stopPropagation}
                            rel="noreferrer"
                            target="_blank">
                            {translate.t('VIEW_IN_EXPLORER')}
                        </a>
                    </li>
                </ul>
                {progressBar}
                <DataTable
                    className="listing-table">
                    <DataTableContent>
                        {getTableHeader()}
                        {getTableBody()}
                    </DataTableContent>
                </DataTable>
                <div activated="false" className="pagination">
                    <div className="controls">
                        <span>{translate.t('ROWS_PER_PAGE', true)}: </span>
                        <Select
                            enhanced
                            onChange={changeRowsPerPage}
                            options={[10, 25, 50, 100]}
                            value = {paginate.displayRows}/>
                        <span>{from} - {to} {translate.t('OF')} {dataLength} </span>
                        <IconButton disabled={!canPrevPage} icon="arrow_back_ios" onClick={prevPage} />
                        <IconButton disabled={!canNextPage} icon="arrow_forward_ios" onClick={nextPage} />
                    </div>
                </div>
            </section>
            <Snackbar
                action={<SnackbarAction icon="close" />}
                leading
                message={snackbarMsg}
                onClose={() => setSnackbarMsg('')}
                open={!!snackbarMsg}
                timeout={2000}/>
            <Footer {...props} />
        </div>
    );
};

export default MasternodeListing;
